import { useState, useCallback } from "react";
import {
    ServiceDBAdminAPIHandler,
    CategorySuggestion,
    GGReview,
    WriteToUs,
} from "../../../services/NetworkClient/BackendAPIs/ServiceDBAdminAPIHandler";

export type FeedbackFilter = "unread" | "read" | "all";

interface UseFeedbackPageState {
    activeTab: number;
    filter: FeedbackFilter;
    isLoading: boolean;

    categorySuggestions: CategorySuggestion[];
    ggReviews: GGReview[];
    writeToUsMessages: WriteToUs[];

    ratingFilter: number | null;
}

interface UseFeedbackPageActions {
    setActiveTab: (tab: number) => void;
    setFilter: (filter: FeedbackFilter) => void;
    setRatingFilter: (rating: number | null) => void;

    fetchCategorySuggestions: () => Promise<void>;
    fetchGGReviews: () => Promise<void>;
    fetchWriteToUsMessages: () => Promise<void>;

    markCategorySuggestionRead: (id: string) => Promise<void>;
    markGGReviewRead: (id: string) => Promise<void>;
    markWriteToUsRead: (id: string) => Promise<void>;

    clearAll: () => void;
}

export type UseFeedbackPageReturn = UseFeedbackPageState & UseFeedbackPageActions;

export const useAdminFeedbackPage = (
    notify: (message: string, isError: boolean) => void
): UseFeedbackPageReturn => {
    const api = ServiceDBAdminAPIHandler.shared;

    const [activeTab, setActiveTab] = useState(0);
    const [filter, setFilter] = useState<FeedbackFilter>("unread");
    const [isLoading, setIsLoading] = useState(false);
    const [ratingFilter, setRatingFilter] = useState<number | null>(null);

    const [categorySuggestions, setCategorySuggestions] = useState<CategorySuggestion[]>([]);
    const [ggReviews, setGGReviews] = useState<GGReview[]>([]);
    const [writeToUsMessages, setWriteToUsMessages] = useState<WriteToUs[]>([]);

    // ---- Category Suggestions ----
    const fetchCategorySuggestions = useCallback(async () => {
        setIsLoading(true);
        try {
            let data: CategorySuggestion[];
            switch (filter) {
                case "unread": data = await api.getUnreadCategorySuggestions(); break;
                case "read": data = await api.getReadCategorySuggestions(); break;
                default: data = await api.getAllCategorySuggestions(); break;
            }
            setCategorySuggestions(data);
            notify(`Fetched ${data.length} category suggestion(s)`, false);
        } catch (err: any) {
            notify(err?.toString() || "Failed to fetch category suggestions", true);
        } finally {
            setIsLoading(false);
        }
    }, [filter, api, notify]);

    // ---- GG Reviews ----
    const fetchGGReviews = useCallback(async () => {
        setIsLoading(true);
        try {
            let data: GGReview[];
            if (ratingFilter && ratingFilter >= 1 && ratingFilter <= 5) {
                data = await api.getGGReviewsByRating(ratingFilter);
            } else {
                switch (filter) {
                    case "unread": data = await api.getUnreadGGReviews(); break;
                    case "read": data = await api.getReadGGReviews(); break;
                    default: data = await api.getAllGGReviews(); break;
                }
            }
            setGGReviews(data);
            notify(`Fetched ${data.length} review(s)`, false);
        } catch (err: any) {
            notify(err?.toString() || "Failed to fetch reviews", true);
        } finally {
            setIsLoading(false);
        }
    }, [filter, ratingFilter, api, notify]);

    // ---- Write To Us ----
    const fetchWriteToUsMessages = useCallback(async () => {
        setIsLoading(true);
        try {
            let data: WriteToUs[];
            switch (filter) {
                case "unread": data = await api.getUnreadWriteToUs(); break;
                case "read": data = await api.getReadWriteToUs(); break;
                default: data = await api.getAllWriteToUs(); break;
            }
            setWriteToUsMessages(data);
            notify(`Fetched ${data.length} message(s)`, false);
        } catch (err: any) {
            notify(err?.toString() || "Failed to fetch messages", true);
        } finally {
            setIsLoading(false);
        }
    }, [filter, api, notify]);

    // ---- Mark as Read ----
    const markCategorySuggestionRead = useCallback(async (id: string) => {
        try {
            await api.markCategorySuggestionAsRead(id);
            setCategorySuggestions(prev => prev.map(s => s._id === id ? { ...s, isRead: true } : s));
            notify("Marked as read", false);
        } catch (err: any) {
            notify(err?.toString() || "Failed to mark as read", true);
        }
    }, [api, notify]);

    const markGGReviewRead = useCallback(async (id: string) => {
        try {
            await api.markGGReviewAsRead(id);
            setGGReviews(prev => prev.map(r => r._id === id ? { ...r, isRead: true } : r));
            notify("Marked as read", false);
        } catch (err: any) {
            notify(err?.toString() || "Failed to mark as read", true);
        }
    }, [api, notify]);

    const markWriteToUsRead = useCallback(async (id: string) => {
        try {
            await api.markWriteToUsAsRead(id);
            setWriteToUsMessages(prev => prev.map(m => m._id === id ? { ...m, isRead: true } : m));
            notify("Marked as read", false);
        } catch (err: any) {
            notify(err?.toString() || "Failed to mark as read", true);
        }
    }, [api, notify]);

    const clearAll = useCallback(() => {
        setCategorySuggestions([]);
        setGGReviews([]);
        setWriteToUsMessages([]);
    }, []);

    return {
        activeTab, setActiveTab,
        filter, setFilter,
        isLoading,
        ratingFilter, setRatingFilter,
        categorySuggestions, ggReviews, writeToUsMessages,
        fetchCategorySuggestions, fetchGGReviews, fetchWriteToUsMessages,
        markCategorySuggestionRead, markGGReviewRead, markWriteToUsRead,
        clearAll,
    };
};
